import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { authApi } from "../../api/auth";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Card } from "../../components/ui/Card";

const forgotPasswordSchema = z.object({
  tenantId: z.string().min(1, "Tenant ID is required"),
  email: z.string().email("Invalid email address"),
});

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setError(null);
    setLoading(true);

    try {
      await authApi.forgotPassword(data.email, data.tenantId);
      navigate(`/reset-password?email=${encodeURIComponent(data.email)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-alt p-4">
      <Card className="w-full max-w-md" title={t("auth.resetYourPassword")}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          <Input
            label={t("auth.tenantId")}
            type="text"
            placeholder={t("auth.tenantIdPlaceholder")}
            error={errors.tenantId?.message}
            {...register("tenantId")}
          />

          <Input
            label={t("auth.email")}
            type="email"
            placeholder={t("auth.emailPlaceholder")}
            error={errors.email?.message}
            {...register("email")}
          />

          <Button type="submit" className="w-full" loading={loading}>
            {t("auth.sendResetLink")}
          </Button>

          <p className="text-center text-sm text-text-muted">
            {t("auth.rememberYourPassword")}{" "}
            <Link to="/login" className="text-brand hover:underline">
              {t("auth.signIn")}
            </Link>
          </p>
        </form>
      </Card>
    </div>
  );
}
